import './GfBorderWrapper.scss';

import React, { CSSProperties, useMemo } from 'react';
import { GfComponentProps } from 'gf-types';
import classNames from 'classnames';

export type GfBorderWrapperProps = GfComponentProps<{
  /** True, to make the gradient colors slide from left to right for 3 seconds infinitely, false otherwise. */
  animate?: boolean;
  borderWidth?: number;
  borderRadius?: number;
  gradientColors?: string[];
  padding?: number | string;
  style?: CSSProperties;
  selected?: boolean;
  selectedBackgroundColor?: string;
  backgroundColor?: string;
}>;

const DEFAULT_GRADIENT_COLORS = ['#0B99C2', '#7E76B5', '#9F2E91', '#29D6C7', '#0B99C2', '#00C2FF'];

/**
 * A wrapper component that applies a gradient border to its children.
 *
 * It can also apply a solid border if only one gradient color is provided.
 *
 * ```
 * Default gradient colors are: ['#0B99C2', '#7E76B5', '#9F2E91', '#29D6C7', '#0B99C2', '#00C2FF']
 * Default border radius is 5px
 * Default border width is 2px
 * Default padding is 2px
 * ```
 *
 * @example
 * ```tsx
 * // Default border wrapper
 * <GfBorderWrapper>
 *   <div>Content</div>
 * </GfBorderWrapper>
 *
 * // custom gradient colors
 * <GfBorderWrapper gradientColors={['#0B99C2', '#7E76B5']}>
 *   <div>Content</div>
 * </GfBorderWrapper>
 *
 * // Custom border wrapper
 * <GfBorderWrapper borderWidth={4} borderRadius={20}>
 *  <div>Content</div>
 * </GfBorderWrapper>
 * ```
 */
export function GfBorderWrapper(props: GfBorderWrapperProps) {
  const {
    animate,
    borderRadius = 5,
    borderWidth = 2,
    children,
    className,
    gradientColors = DEFAULT_GRADIENT_COLORS,
    padding = 2,
    selected = false,
    selectedBackgroundColor = '#fff, #fff',
    backgroundColor = '#fff, #fff',
    style = {}
  } = props;

  const gradient = useMemo(() => `linear-gradient(to right, ${gradientColors.join(', ')})`, [gradientColors]);
  const paddingValue = useMemo(() => (typeof padding === 'number' ? `${padding}px` : padding), [padding]);

  const baseStyle: CSSProperties = useMemo(
    () => ({
      border: `${borderWidth}px solid transparent`,
      borderRadius: `${borderRadius}px`,
      background: `
      linear-gradient(${selected ? selectedBackgroundColor : backgroundColor}) padding-box,
      ${gradient} border-box`,
      padding: paddingValue
    }),
    [gradient, paddingValue, borderWidth, borderRadius, selected, backgroundColor, selectedBackgroundColor]
  );

  return (
    <div className={classNames('gf-border-wrapper', className, { animate })} style={{ ...baseStyle, ...style }} {...props}>
      {children}
    </div>
  );
}
